import React, { useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { api } from '../../utils/api'
import { buildQueueParams, evidencePreview, ruleLabel, sourceLabel } from '../../utils/catalogReview'
import ProductDetailModal from './ProductDetailModal'
import '../../styles/pages.css'

const STATE_OPTIONS = [
  { value: 'auto_created', label: 'Needs review' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'merged', label: 'Merged' }
]

// 'All states' is offered in review mode too (2026-09-03): the canonical row a
// duplicate should merge into is usually confirmed, and hiding it sent the
// operator hunting across tabs. The review DEFAULT stays 'Needs review'.
const BROWSE_STATE_OPTIONS = [
  { value: 'all', label: 'All states' },
  ...STATE_OPTIONS
]

// Origin = the registered class of the source that minted the product.
// Intake (customer submissions) needs operator eyes; catalog imports
// (wiki rosters) arrived through the gated match-then-mint pipeline.
const ORIGIN_OPTIONS = [
  { value: 'intake', label: 'Intake' },
  { value: 'catalog', label: 'Catalog imports' },
  { value: 'all', label: 'All origins' }
]

// Audit = the nightly mint audit's open flags (Prism specs_20260910). The
// flagged view spans every live state, so the state filter is ignored with it.
const AUDIT_OPTIONS = [
  { value: 'all', label: 'All products' },
  { value: 'flagged', label: 'Flagged by the audit' }
]

// Sort keys mirror the backend (review_service): created is newest-first, the
// default; flags surfaces the most-flagged rows first so a flagged list works
// top-down.
const SORT_OPTIONS = [
  { value: 'created', label: 'Newest first' },
  { value: 'name', label: 'Name A-Z' },
  { value: 'flags', label: 'Most flags' }
]

const PAGE_SIZE_OPTIONS = [20, 50, 100]

const THUMB_SIZE = 44

export function stateBadge(reviewState) {
  if (reviewState === 'confirmed') return 'badge badge-success'
  if (reviewState === 'merged') return 'badge badge-neutral'
  return 'badge badge-warning'
}

function CatalogReview({ mode = 'review' }) {
  const browse = mode === 'browse'
  // Browse is read-only for staff (F8); mutations (bulk dismiss, confirm) are admin-only.
  const { user } = useAuth()
  const isAdmin = !!user?.is_admin
  const defaultState = browse ? 'all' : 'auto_created'
  // Review mode defaults to intake so wiki-roster imports (thousands of
  // gated catalog mints) never drown the submissions that need eyes.
  const defaultOrigin = browse ? 'all' : 'intake'

  const [searchParams, setSearchParams] = useSearchParams()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const [items, setItems] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [reviewState, setReviewState] = useState(searchParams.get('state') || defaultState)
  const [origin, setOrigin] = useState(searchParams.get('origin') || defaultOrigin)
  const [audit, setAudit] = useState(searchParams.get('audit') || 'all')
  const [sort, setSort] = useState(searchParams.get('sort') || 'created')
  const [category, setCategory] = useState(searchParams.get('category') || '')
  const [source, setSource] = useState(searchParams.get('source') || '')
  const [searchInput, setSearchInput] = useState('')
  const [q, setQ] = useState('')
  // Dropdown vocabulary from the backend: live source keys + the category
  // tree. Loaded once; a failure leaves the two selects with no options,
  // never an error banner (the list still works without them).
  const [filterOptions, setFilterOptions] = useState({ sources: [], categories: [] })

  // Selected product for the detail modal
  const [selectedId, setSelectedId] = useState(null)

  // Bulk selection for "Dismiss selected" on the flagged view, keyed by
  // product id. Cleared on every reload so it never spans filter changes.
  const [selectedIds, setSelectedIds] = useState(() => new Set())
  const [bulkBusy, setBulkBusy] = useState(false)

  const loadQueue = useCallback(async () => {
    setLoading(true)
    setError(null)
    setSelectedIds(new Set())
    try {
      const params = buildQueueParams({ reviewState, page, pageSize, q, origin, audit, category, source, sort })
      const response = await api.get(`/api/graph/review/products?${params}`)
      setItems(response.items || [])
      setTotal(response.total || 0)
    } catch (err) {
      console.error('Failed to load review queue:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [reviewState, page, pageSize, q, origin, audit, category, source, sort])

  useEffect(() => {
    loadQueue()
  }, [loadQueue])

  useEffect(() => {
    let cancelled = false
    api.get('/api/graph/review/filters')
      .then(response => {
        if (!cancelled) {
          setFilterOptions({ sources: response.sources || [], categories: response.categories || [] })
        }
      })
      .catch(err => { if (!cancelled) console.warn('No filter vocabulary:', err.message) })
    return () => { cancelled = true }
  }, [])

  function syncUrl(next) {
    const merged = { reviewState, origin, audit, category, source, sort, ...next }
    const params = {}
    if (merged.reviewState !== defaultState) params.state = merged.reviewState
    if (merged.origin !== defaultOrigin) params.origin = merged.origin
    if (merged.audit !== 'all') params.audit = merged.audit
    if (merged.category) params.category = merged.category
    if (merged.source) params.source = merged.source
    if (merged.sort && merged.sort !== 'created') params.sort = merged.sort
    setSearchParams(params)
  }

  function applyFilter(setter, key) {
    return value => {
      setter(value)
      setPage(1)
      syncUrl({ [key]: value })
    }
  }

  const handleStateChange = applyFilter(setReviewState, 'reviewState')
  const handleOriginChange = applyFilter(setOrigin, 'origin')
  const handleAuditChange = applyFilter(setAudit, 'audit')
  const handleCategoryChange = applyFilter(setCategory, 'category')
  const handleSourceChange = applyFilter(setSource, 'source')
  const handleSortChange = applyFilter(setSort, 'sort')

  function handlePageSizeChange(value) {
    setPageSize(value)
    setPage(1)
  }

  function toggleSelected(id) {
    setSelectedIds(current => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function toggleSelectAll(ids, checked) {
    setSelectedIds(checked ? new Set(ids) : new Set())
  }

  async function handleDismissSelected() {
    const chosen = items.filter(item => selectedIds.has(item.product_model_id))
    const flagIds = chosen.flatMap(item => (item.audit_flags || []).map(f => f.model_audit_id))
    if (flagIds.length === 0) return
    setBulkBusy(true)
    setError(null)
    try {
      // Loop per flag: each keeps its own ledger line and its own refusal on
      // the backend (spec §3.3); one failure never aborts the rest.
      const results = await Promise.allSettled(
        flagIds.map(id =>
          api.post(`/api/graph/review/audit/${id}/resolve`, { resolution: 'dismissed' })
        )
      )
      const failed = results.filter(r => r.status === 'rejected').length
      if (failed > 0) setError(`${failed} of ${flagIds.length} flags could not be dismissed.`)
      await loadQueue()
    } catch (err) {
      console.error('Bulk dismiss failed:', err)
      setError(err.message)
    } finally {
      setBulkBusy(false)
    }
  }

  function handleSearch() {
    setPage(1)
    setQ(searchInput)
  }

  async function handleQuickConfirm(event, item) {
    event.stopPropagation()
    try {
      await api.post(`/api/graph/review/products/${item.product_model_id}/confirm`)
      loadQueue()
    } catch (err) {
      console.error('Failed to confirm product:', err)
      setError(err.message)
    }
  }

  function handleModalMutated() {
    loadQueue()
  }

  const totalPages = Math.ceil(total / pageSize)
  // Bulk dismiss lives on the flagged view, where every row carries flags to act
  // on, and only for admins (it mutates; staff Browse is read-only).
  const flaggedView = audit === 'flagged' && isAdmin
  const flaggedIdsOnPage = items
    .filter(item => (item.audit_flags || []).length > 0)
    .map(item => item.product_model_id)
  const allSelected = flaggedIdsOnPage.length > 0 && flaggedIdsOnPage.every(id => selectedIds.has(id))

  return (
    <>
      <div className="text-sm text-muted" style={{ marginBottom: 'var(--space-3)' }}>
        {browse ? (
          <>Every product in the knowledge graph. Search by name; filter by state,
          origin, audit flag, category and source. Click a row for evidence, photos
          and actions.</>
        ) : (
          <>Products the engine created from customer submissions. <strong>Confirm</strong> = the
          identity is right · <strong>Correct</strong> = fix the brand/model · <strong>Merge</strong> = this
          is a duplicate of an existing product (reversible). Click a row for the
          customer's own words and photos.</>
        )}
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
        <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div className="filter-group" style={{ width: 180 }}>
            <label className="filter-label">State</label>
            <select
              className="form-input"
              value={reviewState}
              onChange={e => handleStateChange(e.target.value)}
            >
              {BROWSE_STATE_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div className="filter-group" style={{ width: 180 }}>
            <label className="filter-label">Origin</label>
            <select
              className="form-input"
              value={origin}
              onChange={e => handleOriginChange(e.target.value)}
            >
              {ORIGIN_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div className="filter-group" style={{ width: 200 }}>
            <label className="filter-label">Audit</label>
            <select
              className="form-input"
              value={audit}
              onChange={e => handleAuditChange(e.target.value)}
            >
              {AUDIT_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div className="filter-group" style={{ width: 220 }}>
            <label className="filter-label">Category</label>
            <select
              className="form-input"
              value={category}
              onChange={e => handleCategoryChange(e.target.value)}
            >
              <option value="">All categories</option>
              {filterOptions.categories.map(group => (
                <optgroup key={group.main} label={group.main}>
                  <option value={group.main}>All {group.main}</option>
                  {group.subs.map(sub => (
                    <option key={sub} value={sub}>{sub}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
          <div className="filter-group" style={{ width: 180 }}>
            <label className="filter-label">Source</label>
            <select
              className="form-input"
              value={source}
              onChange={e => handleSourceChange(e.target.value)}
            >
              <option value="">All sources</option>
              {filterOptions.sources.map(key => (
                <option key={key} value={key}>{sourceLabel(key)}</option>
              ))}
            </select>
          </div>
          <div className="filter-group" style={{ width: 150 }}>
            <label className="filter-label">Sort</label>
            <select
              className="form-input"
              value={sort}
              onChange={e => handleSortChange(e.target.value)}
            >
              {SORT_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div className="filter-group" style={{ width: 100 }}>
            <label className="filter-label">Per page</label>
            <select
              className="form-input"
              value={pageSize}
              onChange={e => handlePageSizeChange(Number(e.target.value))}
            >
              {PAGE_SIZE_OPTIONS.map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
          <div className="filter-group" style={{ width: 240 }}>
            <label className="filter-label">Search</label>
            <input
              className="form-input"
              type="text"
              placeholder="Product name…"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSearch() }}
            />
          </div>
          <button type="button" className="btn btn-secondary" onClick={handleSearch}>
            Search
          </button>
          {total > 0 && (
            <span className="text-sm text-muted" style={{ marginLeft: 'auto' }}>
              {total} product{total !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      {error && (
        <div className="card" style={{ background: 'var(--color-error-light)', padding: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
          <strong>Error:</strong> {error}
          <button
            type="button"
            className="btn btn-sm"
            style={{ marginLeft: 'var(--space-3)' }}
            onClick={() => setError(null)}
          >
            Dismiss
          </button>
        </div>
      )}

      <div className="card">
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-12)' }}>
            <div className="loading-spinner" />
          </div>
        ) : items.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-title">No Products Found</div>
            <div className="empty-state-description">
              {q
                ? 'Nothing matches this search with the selected filters.'
                : audit === 'flagged'
                  ? 'No open audit flags with these filters. The audit runs nightly at 04:10.'
                  : !browse && reviewState === 'auto_created'
                    ? 'The review queue is empty — every machine-created product has been reviewed.'
                    : 'No products match these filters yet.'}
            </div>
          </div>
        ) : (
          <>
            {flaggedView && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', padding: 'var(--space-2) var(--space-3)', borderBottom: '1px solid var(--color-border)' }}>
                <span className="text-sm text-muted">
                  {selectedIds.size > 0 ? `${selectedIds.size} selected` : 'Tick rows to dismiss their flags together'}
                </span>
                <button
                  type="button"
                  className="btn btn-sm btn-secondary"
                  style={{ marginLeft: 'auto' }}
                  disabled={selectedIds.size === 0 || bulkBusy}
                  onClick={handleDismissSelected}
                >
                  {bulkBusy ? 'Dismissing…' : `Dismiss selected${selectedIds.size ? ` (${selectedIds.size})` : ''}`}
                </button>
              </div>
            )}
          <table className="table">
            <thead>
              <tr>
                {flaggedView && (
                  <th style={{ width: 32 }}>
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={e => toggleSelectAll(flaggedIdsOnPage, e.target.checked)}
                      aria-label="Select all flagged rows on this page"
                    />
                  </th>
                )}
                <th style={{ width: THUMB_SIZE + 8 }}></th>
                <th>Product</th>
                <th>Brand</th>
                <th>Model</th>
                <th>Category</th>
                <th>Evidence</th>
                <th>Flags</th>
                <th style={{ width: 110, textAlign: 'right' }}></th>
              </tr>
            </thead>
            <tbody>
              {items.map(item => (
                <tr
                  key={item.product_model_id}
                  onClick={() => setSelectedId(item.product_model_id)}
                  style={{ cursor: 'pointer' }}
                >
                  {flaggedView && (
                    <td style={{ padding: 'var(--space-2)' }} onClick={e => e.stopPropagation()}>
                      {(item.audit_flags || []).length > 0 && (
                        <input
                          type="checkbox"
                          checked={selectedIds.has(item.product_model_id)}
                          onChange={() => toggleSelected(item.product_model_id)}
                          aria-label="Select row for bulk dismiss"
                        />
                      )}
                    </td>
                  )}
                  <td style={{ padding: 'var(--space-2)' }}>
                    {item.hero_url ? (
                      <img
                        src={item.hero_url}
                        alt=""
                        loading="lazy"
                        style={{ width: THUMB_SIZE, height: THUMB_SIZE, objectFit: 'cover', borderRadius: 4, background: 'var(--color-bg-tertiary)', display: 'block' }}
                      />
                    ) : (
                      <div
                        title="No photo in the bank yet"
                        style={{ width: THUMB_SIZE, height: THUMB_SIZE, borderRadius: 4, background: 'var(--color-bg-tertiary)' }}
                      />
                    )}
                  </td>
                  <td>
                    <div style={{ fontWeight: 500 }}>{item.display_name}</div>
                    <div className="text-sm text-muted">
                      {item.model_code}
                      {item.source ? ` · ${sourceLabel(item.source)}` : ''}
                    </div>
                  </td>
                  <td>{item.brand || '-'}</td>
                  <td>{item.model || '-'}</td>
                  <td>{item.category || '-'}</td>
                  <td>
                    <div>{evidencePreview(item.declared_names)}</div>
                    <div className="text-sm text-muted">
                      {item.evidence_count} observation{item.evidence_count !== 1 ? 's' : ''}
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 'var(--space-1)', flexWrap: 'wrap' }}>
                      {(item.audit_flags || []).map(flag => (
                        <span key={flag.model_audit_id} className="badge badge-warning badge-sm" title={flag.detail}>
                          {ruleLabel(flag.rule)}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {!browse && item.review_state === 'auto_created' ? (
                      <button
                        type="button"
                        className="btn btn-sm btn-secondary"
                        onClick={e => handleQuickConfirm(e, item)}
                      >
                        Confirm
                      </button>
                    ) : (
                      <span className={stateBadge(item.review_state)}>{item.review_state}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </>
        )}

        {/* Pagination */}
        {items.length > 0 && totalPages > 1 && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: 'var(--space-3)',
            borderTop: '1px solid var(--color-border)'
          }}>
            <span className="text-sm text-muted">
              Page {page} of {totalPages} ({total} total)
            </span>
            <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
              <button
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </button>
              <button
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {selectedId && (
        <ProductDetailModal
          productModelId={selectedId}
          onClose={() => setSelectedId(null)}
          onMutated={handleModalMutated}
        />
      )}
    </>
  )
}

export default CatalogReview
